import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  Film,
  Bot,
  Wallet,
  Check,
  ArrowRight,
  Youtube,
  Instagram,
  Music2,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BrandLogo } from "@/components/BrandLogo";
import beforeAfterOriginal from "@/assets/before-after-original.webp";
import stepImg1 from "@/assets/howto/step-01-video.jpg";
import stepImg2 from "@/assets/howto/step-02-image.jpg";
import stepImg3 from "@/assets/howto/step-03-ai.jpg";


import { seoHead } from "@/lib/seo";
import { SHOW_TRIAL_PLAN } from "@/lib/featureFlags";
import { captureReferralCodeFromUrl } from "@/lib/referral";

export const Route = createFileRoute("/")({
  head: () => {
    const base = seoHead({
      title: "KAGEM - AIで作る変身動画 | 写真1枚で誰でもクリエイター",
      description:
        "日本語対応のAI変身動画作成サービス。写真と動画をアップロードするだけで、簡単に高品質な変身動画を生成。月額プランのクレジット制で気軽に始められます。",
      path: "/",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          // TODO: aggregateRating（ユーザーレビュー集計）が用意できたら追加
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "KAGEM",
            description: "AIで動画の主役を入れ替える変身動画作成サービス",
            applicationCategory: "MultimediaApplication",
            operatingSystem: "Web Browser",
            offers: {
              "@type": "Offer",
              price: "500",
              priceCurrency: "JPY",
            },
            inLanguage: "ja",
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: LandingPage,
});

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" as const },
};

function LazyVideo({ src, className }: { src: string; className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || shouldLoad) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShouldLoad(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shouldLoad]);

  return (
    <div ref={ref} className={className}>
      {shouldLoad ? (
        <video
          src={src}
          className="h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
        />
      ) : (
        <div className="h-full w-full bg-black" />
      )}
    </div>
  );
}

function LandingPage() {

  const navigate = useNavigate();

  useEffect(() => {
    // Capture ?ref=CODE early so it survives navigation to /signup.
    captureReferralCodeFromUrl();
    let mounted = true;
    // 認証チェックはバックグラウンドで実行（Heroをブロックしない）
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      if (data.session) {
        navigate({ to: "/dashboard", replace: true });
      }
    });
    return () => {
      mounted = false;
    };
  }, [navigate]);


  return (
    <div className="kagem-root min-h-screen bg-background text-foreground" style={{ paddingBottom: "max(0px, env(safe-area-inset-bottom))" }}>
      <SiteHeader />
      <Hero />
      <Showcase />
      <HowItWorks />
      <UseCases />
      <WhyKagem />
      <SocialProof />
      <Features />
      <Demo />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
      <MobileStickyCTA />
    </div>
  );
}

function MobileStickyCTA() {
  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .kagem-root { padding-bottom: calc(76px + env(safe-area-inset-bottom)) !important; }
          .kagem-mobile-sticky-cta { display: flex !important; }
        }
        @media (min-width: 769px) {
          .kagem-mobile-sticky-cta { display: none !important; }
        }
        .kagem-mobile-sticky-cta-btn:hover { transform: translateY(-1px); box-shadow: 0 0 0 1px rgba(255,255,255,0.5), 0 12px 32px rgba(255,255,255,0.22); }
        .kagem-header-trial-cta { display: none; }
        @media (min-width: 769px) {
          .kagem-header-trial-cta { display: inline-flex !important; }
        }
        .kagem-header-trial-cta:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(255,255,255,0.2); }
        .kagem-inline-cta-primary:hover { transform: translateY(-1px); box-shadow: 0 12px 32px rgba(255,255,255,0.22); }
        .kagem-inline-cta-ghost:hover { border-color: #fff !important; background: rgba(255,255,255,0.05) !important; }
      `}</style>
      <div
        className="kagem-mobile-sticky-cta"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(0,0,0,0.92)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderTop: "1px solid rgba(255,255,255,0.12)",
          padding: "12px 16px calc(12px + env(safe-area-inset-bottom)) 16px",
          alignItems: "center",
          gap: 12,
          display: "none",
        }}
      >
        {SHOW_TRIAL_PLAN && (
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
            <span style={{ fontFamily: '"Chakra Petch", sans-serif', fontSize: 18, fontWeight: 600, color: "#fff" }}>¥500</span>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", letterSpacing: "0.1em" }}>お試し1本</span>
          </div>
        )}
        <Link
          to="/signup"
          className="kagem-mobile-sticky-cta-btn"
          style={{
            flex: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
            color: "#000",
            padding: "12px 20px",
            borderRadius: 999,
            fontFamily: '"Chakra Petch", sans-serif',
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: "0.02em",
            boxShadow: "0 0 0 1px rgba(255,255,255,0.4), 0 10px 30px rgba(255,255,255,0.15)",
            transition: "all 0.15s ease",
            textDecoration: "none",
          }}
        >
          変身を試す →
        </Link>
      </div>
    </>
  );
}

function SiteHeader() {
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-border/40 bg-background/70 backdrop-blur-lg">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <BrandLogo size="sm" />
        <div className="flex items-center gap-2 sm:gap-3">
          <ThemeToggle />
          <Button asChild variant="ghost" size="sm">
            <Link to="/login" search={{ redirect: undefined, banned: undefined }}>ログイン</Link>
          </Button>
          <Button asChild size="sm" className="bg-gradient-brand">
            <Link to="/signup">新規登録</Link>
          </Button>
          {SHOW_TRIAL_PLAN && (
          <Link
            to="/signup"
            className="kagem-header-trial-cta items-center justify-center"
            style={{
              background: "#fff",
              color: "#000",
              padding: "10px 20px",
              borderRadius: 999,
              fontFamily: '"Chakra Petch", sans-serif',
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.02em",
              boxShadow: "0 0 0 1px rgba(255,255,255,0.3)",
              transition: "all 0.15s ease",
              textDecoration: "none",
            }}
          >
            ¥500でお試し →
          </Link>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const particlesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = particlesRef.current;
    if (!container) return;
    container.innerHTML = "";
    for (let i = 0; i < 90; i++) {
      const s = document.createElement("span");
      s.style.left = Math.random() * 100 + "%";
      s.style.top = Math.random() * 100 + "%";
      s.style.animationDuration = 5 + Math.random() * 10 + "s";
      s.style.animationDelay = Math.random() * 10 + "s";
      container.appendChild(s);
    }
  }, []);

  const scrollToDemo = () => {
    document.getElementById("showcase")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="relative flex items-center justify-center overflow-hidden"
      style={{ minHeight: "100vh", background: "#000", padding: "clamp(80px, 12vw, 120px) 0 clamp(48px, 8vw, 80px)" }}
    >
      {/* Layer 1 — central radial glow */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 70% at 50% 45%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 30%, transparent 70%)",
          zIndex: 0,
        }}
      />
      {/* Layer 2 — dot grid */}
      <div className="dot-grid" aria-hidden="true" />
      {/* Layer 3 — particles */}
      <div className="particles" aria-hidden="true" ref={particlesRef} />

      <div className="relative z-10 mx-auto w-full max-w-3xl px-4 text-center">
        {/* Pill tagline */}
        <div
          className="mx-auto inline-flex items-center gap-2"
          style={{
            padding: "6px 16px",
            borderRadius: 999,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.18)",
            fontFamily: '"Chakra Petch", sans-serif',
            fontSize: 11,
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.72)",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: "#fff",
              boxShadow: "0 0 12px rgba(255,255,255,0.9)",
              animation: "kagemDotPulse 2s ease-in-out infinite",
            }}
          />
          NEXT-GEN AI TRANSFORMATION
        </div>

        {/* Main headline */}
        <h1
          className="mt-8"
          style={{
            fontFamily: '"Chakra Petch", "Noto Sans JP", sans-serif',
            fontWeight: 300,
            fontSize: "clamp(34px, 5.2vw, 68px)",
            letterSpacing: "0.04em",
            lineHeight: 1.2,
            color: "#fff",
            textShadow: "0 0 50px rgba(255,255,255,0.18)",
          }}
        >
          画像があれば、
          <br />
          <span
            style={{
              fontWeight: 500,
              textShadow: "0 0 40px rgba(255,255,255,0.4)",
            }}
          >
            誰にでもなれる。
          </span>
        </h1>

        {/* Subheadline */}
        <p
          className="mx-auto mt-6"
          style={{
            fontFamily: '"Noto Sans JP", sans-serif',
            fontSize: "clamp(15px, 1.3vw, 17px)",
            color: "rgba(255,255,255,0.72)",
            maxWidth: 560,
            lineHeight: 1.95,
          }}
        >
          あなたの動画と、なりたい人の素材を1枚。
          <br />
          それだけで、AIがあなたを、その人物に変身させる。
        </p>

        {/* Brand line */}
        <div
          className="mt-10"
          style={{
            fontFamily: '"Chakra Petch", sans-serif',
            fontSize: 11,
            letterSpacing: "0.35em",
            color: "rgba(255,255,255,0.32)",
            textTransform: "uppercase",
            marginBottom: 44,
          }}
        >
          KAGEM — &quot;もう1人の自分&quot;を生成する
        </div>

        {/* CTAs */}
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/signup"
            className="kagem-cta-primary inline-flex items-center justify-center"
            style={{
              background: "#fff",
              color: "#000",
              padding: "16px 30px",
              borderRadius: 999,
              fontFamily: '"Chakra Petch", sans-serif',
              fontSize: 14,
              fontWeight: 500,
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
          >
            はじめる →
          </Link>
          <button
            type="button"
            onClick={scrollToDemo}
            className="kagem-cta-secondary inline-flex items-center justify-center"
            style={{
              background: "transparent",
              color: "#fff",
              padding: "16px 30px",
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.18)",
              fontFamily: '"Chakra Petch", sans-serif',
              fontSize: 14,
              fontWeight: 500,
              transition: "border-color 0.2s ease, background 0.2s ease",
              cursor: "pointer",
            }}
          >
            ▶ サンプルを見る（登録不要）
          </button>
        </div>

        {/* Trust badges */}
        <div
          className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2"
          style={{
            fontFamily: '"Noto Sans JP", sans-serif',
            fontSize: 12,
            color: "rgba(255,255,255,0.5)",
          }}
        >
          <span>✓ 会員登録なしでサンプル視聴可能</span>
          <span>✓ 全身変身対応（顔だけじゃない）</span>
          <span>✓ 音声付き高画質1080p</span>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      num: "STEP 01",
      title: "あなたの動画をアップロード",
      desc: "全身が映った10秒前後のスマホ動画でOK。動きや表情をそのままに、見た目だけを変えます。",
      image: stepImg1,
      alt: "スマホで全身動画を撮影するイメージ",
    },
    {
      num: "STEP 02",
      title: "なりたい人の素材をアップロード",
      desc: "画像1枚あればOK。写真・AI生成画像・イラスト、なんでも自由に選べます。",
      image: stepImg2,
      alt: "なりたい人の参照画像1枚をアップロードするイメージ",
    },
    {
      num: "STEP 03",
      title: "AIが変身動画を生成",
      desc: "独自AI技術が約3分で音声付き高画質動画を出力。SNSへすぐシェアできます。",
      image: stepImg3,
      alt: "AIが変身動画を生成するイメージ",
    },
  ];

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "#000", padding: "clamp(72px, 11vw, 140px) 0" }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(80% 60% at 50% 0%, rgba(255,255,255,0.04), transparent 50%)",
          pointerEvents: "none",
        }}
      />

      <div className="relative z-10 mx-auto max-w-6xl px-4">
        <div className="text-center">
          <div
            style={{
              fontFamily: '"Chakra Petch", sans-serif',
              fontSize: 11,
              letterSpacing: "0.35em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.5)",
            }}
          >
            HOW IT WORKS
          </div>
          <h2
            className="mt-4"
            style={{
              fontFamily: '"Chakra Petch", "Noto Sans JP", sans-serif',
              fontWeight: 300,
              fontSize: "clamp(28px, 3.6vw, 44px)",
              letterSpacing: "0.04em",
              color: "#fff",
              lineHeight: 1.3,
            }}
          >
            3ステップで、もう1人の自分。
          </h2>
          <p
            className="mx-auto mt-4"
            style={{
              fontFamily: '"Noto Sans JP", sans-serif',
              fontSize: 15,
              color: "rgba(255,255,255,0.72)",
              maxWidth: 540,
              lineHeight: 1.85,
            }}
          >
            複雑な設定は一切なし。あなたの動画と、なりたい人の素材があればOK。
          </p>
        </div>

        <div
          className="mt-16 grid gap-6"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}
        >
          {steps.map((s) => (
            <div
              key={s.num}
              className="kagem-step-card"
              style={{
                padding: "36px 30px",
                background: "rgba(255,255,255,0.025)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
              }}
            >
              <div
                style={{
                  fontFamily: '"Chakra Petch", sans-serif',
                  fontSize: 14,
                  letterSpacing: "0.3em",
                  color: "rgba(255,255,255,0.5)",
                }}
              >
                {s.num}
              </div>
              <h3
                className="mt-3"
                style={{
                  fontFamily: '"Noto Sans JP", sans-serif',
                  fontSize: 18,
                  fontWeight: 600,
                  color: "#fff",
                  lineHeight: 1.5,
                }}
              >
                {s.title}
              </h3>
              <p
                className="mt-3"
                style={{
                  fontFamily: '"Noto Sans JP", sans-serif',
                  fontSize: 13.5,
                  color: "rgba(255,255,255,0.72)",
                  lineHeight: 1.85,
                }}
              >
                {s.desc}
              </p>

              <div
                className="mt-7 relative overflow-hidden"
                style={{
                  aspectRatio: "16 / 10",
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <img
                  src={s.image}
                  alt={s.alt}
                  loading="lazy"
                  width={1024}
                  height={640}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.5) 100%)",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 48, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          <p style={{ fontFamily: '"Noto Sans JP", sans-serif', fontSize: 14, color: "rgba(255,255,255,0.6)", marginBottom: 16 }}>
            3分後には、もう1人の自分が完成。
          </p>
          <Link
            to="/signup"
            className="kagem-inline-cta-primary inline-flex items-center justify-center"
            style={{
              background: "#fff",
              color: "#000",
              padding: "14px 26px",
              borderRadius: 999,
              fontFamily: '"Chakra Petch", sans-serif',
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "0.02em",
              transition: "all 0.15s ease",
              textDecoration: "none",
            }}
          >
            いますぐ3ステップで作る →
          </Link>
        </div>
      </div>
    </section>
  );
}

function StepVisual({ kind }: { kind: "video" | "single" | "play" }) {
  if (kind === "video") {
    return (
      <>
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)",
            backgroundSize: "16px 16px",
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <svg
            width="56"
            height="56"
            viewBox="0 0 24 24"
            fill="none"
            stroke="rgba(255,255,255,0.85)"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m10 9 5 3-5 3z" fill="rgba(255,255,255,0.85)" />
          </svg>
        </div>
      </>
    );
  }
  if (kind === "single") {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <div
          className="flex items-center justify-center"
          style={{
            width: "55%",
            aspectRatio: "4 / 5",
            borderRadius: 8,
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.18)",
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </div>
        <div
          style={{
            fontFamily: '"Chakra Petch", sans-serif',
            fontSize: 9,
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.5)",
          }}
        >
          IMAGE × 1
        </div>
      </div>
    );
  }
  return (
    <>
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(40% 50% at 50% 50%, rgba(255,255,255,0.18), transparent 70%)",
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="flex items-center justify-center"
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.4)",
            background: "rgba(255,255,255,0.05)",
            boxShadow: "0 0 40px rgba(255,255,255,0.25)",
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    </>
  );
}

// TODO: 実際のBefore/After動画に差し替え
type BeforeAfterPair = {
  id: string;
  label: string;
  before: string;
  after: string;
  thumbBefore: string;
  thumbAfter: string;
};
const BEFORE_AFTER_PAIRS: BeforeAfterPair[] = [
  {
    id: "pair-1",
    label: "女性 → 男性",
    before: "/lp/ba-before-1.mp4",
    after: "/lp/ba-after-1.mp4",
    thumbBefore: "/lp/ba-before-1.jpg",
    thumbAfter: "/lp/ba-after-1.jpg",
  },
  {
    id: "pair-2",
    label: "20代 → 60代",
    before: "/lp/ba-before-2.mp4",
    after: "/lp/ba-after-2.mp4",
    thumbBefore: "/lp/ba-before-2.jpg",
    thumbAfter: "/lp/ba-after-2.jpg",
  },
  {
    id: "pair-4",
    label: "ボディメイク",
    before: "/lp/ba-before-2.mp4",
    after: "/lp/ba-after-4.mp4",
    thumbBefore: "/lp/ba-before-2.jpg",
    thumbAfter: "/lp/ba-after-4.jpg",
  },
  {
    id: "pair-3",
    label: "普段 → 推し風",
    before: "/lp/ba-before-2.mp4",
    after: "/lp/ba-after-3.mp4",
    thumbBefore: "/lp/ba-before-2.jpg",
    thumbAfter: "/lp/ba-after-3.jpg",
  },
];

function Showcase() {
  const [currentId, setCurrentId] = useState<string>(BEFORE_AFTER_PAIRS[0].id);
  const [fading, setFading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [loadingBefore, setLoadingBefore] = useState(true);
  const [loadingAfter, setLoadingAfter] = useState(true);
  const current =
    BEFORE_AFTER_PAIRS.find((p) => p.id === currentId) ?? BEFORE_AFTER_PAIRS[0];

  const beforeVideoRef = useRef<HTMLVideoElement | null>(null);
  const afterVideoRef = useRef<HTMLVideoElement | null>(null);
  const readyRef = useRef<{ before: boolean; after: boolean }>({ before: false, after: false });

  const isBeforeVideo = !/\.(png|jpe?g|webp|gif|avif)$/i.test(current.before);
  const isAfterVideo = !/\.(png|jpe?g|webp|gif|avif)$/i.test(current.after);

  const trySync = () => {
    const b = beforeVideoRef.current;
    const a = afterVideoRef.current;
    const needBefore = isBeforeVideo;
    const needAfter = isAfterVideo;
    if (needBefore && (!b || !readyRef.current.before)) return;
    if (needAfter && (!a || !readyRef.current.after)) return;
    try {
      if (b) { b.pause(); b.currentTime = 0; }
      if (a) { a.pause(); a.currentTime = 0; }
      const plays: Promise<void>[] = [];
      if (b) plays.push(b.play());
      if (a) plays.push(a.play());
      void Promise.all(plays).catch(() => {});
    } catch {
      /* noop */
    }
  };

  const handleEnded = () => {
    if (playing) trySync();
  };

  const handlePlayClick = () => {
    if (playing) {
      beforeVideoRef.current?.pause();
      afterVideoRef.current?.pause();
      setPlaying(false);
      return;
    }
    setLoadingBefore(isBeforeVideo);
    setLoadingAfter(isAfterVideo);
    setPlaying(true);
    requestAnimationFrame(() => trySync());
  };

  useEffect(() => {
    readyRef.current = { before: false, after: false };
    setPlaying(false);
    setLoadingBefore(true);
    setLoadingAfter(true);
  }, [currentId]);

  const handleSelect = (id: string) => {
    if (id === currentId) return;
    setFading(true);
    window.setTimeout(() => {
      setCurrentId(id);
      setFading(false);
    }, 200);
  };

  const labelStyle = (variant: "before" | "after"): React.CSSProperties => ({
    position: "absolute",
    top: 12,
    left: 12,
    padding: "6px 14px",
    borderRadius: 999,
    background:
      variant === "after" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.7)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: "1px solid rgba(255,255,255,0.18)",
    fontFamily: '"Chakra Petch", sans-serif',
    fontSize: 10,
    letterSpacing: "0.3em",
    textTransform: "uppercase",
    color: "#fff",
    zIndex: 2,
  });

  const videoBoxStyle: React.CSSProperties = {
    position: "relative",
    aspectRatio: "9 / 16",
    background: "#000",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 20,
    overflow: "hidden",
  };

  return (
    <section
      id="showcase"
      className="relative overflow-hidden"
      style={{ background: "#000", padding: "clamp(72px, 11vw, 140px) 0" }}
    >
      <div className="relative z-10 mx-auto max-w-6xl px-4">
        <div className="text-center">
          <div
            style={{
              fontFamily: '"Chakra Petch", sans-serif',
              fontSize: 11,
              letterSpacing: "0.35em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.5)",
            }}
          >
            BEFORE / AFTER
          </div>
          <h2
            className="mt-4"
            style={{
              fontFamily: '"Chakra Petch", "Noto Sans JP", sans-serif',
              fontWeight: 300,
              fontSize: "clamp(28px, 3.6vw, 44px)",
              letterSpacing: "0.04em",
              color: "#fff",
              lineHeight: 1.3,
            }}
          >
            画像1枚で、別人になる。
          </h2>
          <p
            className="mx-auto mt-4"
            style={{
              fontFamily: '"Noto Sans JP", sans-serif',
              fontSize: 15,
              color: "rgba(255,255,255,0.72)",
              maxWidth: 620,
              lineHeight: 1.85,
            }}
          >
            あなたの動画と、なりたい人の素材を1枚。同じあなたが、こんなに別人に変身します。
          </p>
        </div>

        {/* Before / After pair */}
        <div
          className="relative mx-auto mt-14 grid"
          style={{
            gridTemplateColumns: "1fr",
            gap: 12,
            maxWidth: 880,
          }}
        >
          <div
            className="kagem-ba-grid relative grid"
            style={{
              gap: 12,
            }}
          >
            <div
              className="kagem-fade-swap"
              style={{ ...videoBoxStyle, opacity: fading ? 0 : 1 }}
            >
              <span style={labelStyle("before")}>BEFORE</span>
              {/\.(png|jpe?g|webp|gif|avif)$/i.test(current.before) ? (
                <img
                  key={`b-${current.id}`}
                  src={current.before}
                  alt="Before"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : playing ? (
                <>
                  <video
                    key={`b-${current.id}`}
                    ref={beforeVideoRef}
                    src={current.before}
                    poster={current.thumbBefore}
                    muted
                    playsInline
                    preload="auto"
                    onLoadStart={() => setLoadingBefore(true)}
                    onWaiting={() => setLoadingBefore(true)}
                    onStalled={() => setLoadingBefore(true)}
                    onCanPlay={() => setLoadingBefore(false)}
                    onPlaying={() => setLoadingBefore(false)}
                    onLoadedData={() => {
                      readyRef.current.before = true;
                      trySync();
                    }}
                    onEnded={handleEnded}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="video-loader" data-loading={loadingBefore ? "true" : "false"}>
                    <div className="video-loader-spinner" />
                  </div>
                </>
              ) : (
                <img
                  key={`b-poster-${current.id}`}
                  src={current.thumbBefore}
                  alt="Before"
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}
            </div>
            <div
              className="kagem-fade-swap"
              style={{ ...videoBoxStyle, opacity: fading ? 0 : 1 }}
            >
              <span style={labelStyle("after")}>AFTER</span>
              {/\.(png|jpe?g|webp|gif|avif)$/i.test(current.after) ? (
                <img
                  key={`a-${current.id}`}
                  src={current.after}
                  alt="After"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : playing ? (
                <>
                  <video
                    key={`a-${current.id}`}
                    ref={afterVideoRef}
                    src={current.after}
                    poster={current.thumbAfter}
                    muted
                    playsInline
                    preload="auto"
                    onLoadStart={() => setLoadingAfter(true)}
                    onWaiting={() => setLoadingAfter(true)}
                    onStalled={() => setLoadingAfter(true)}
                    onCanPlay={() => setLoadingAfter(false)}
                    onPlaying={() => setLoadingAfter(false)}
                    onLoadedData={() => {
                      readyRef.current.after = true;
                      trySync();
                    }}
                    onEnded={handleEnded}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="video-loader" data-loading={loadingAfter ? "true" : "false"}>
                    <div className="video-loader-spinner" />
                  </div>
                </>
              ) : (
                <img
                  key={`a-poster-${current.id}`}
                  src={current.thumbAfter}
                  alt="After"
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}
            </div>

            {/* Play / Pause toggle (centered overlap) */}
            <button
              type="button"
              onClick={handlePlayClick}
              aria-label={playing ? "一時停止" : "再生"}
              className="absolute flex items-center justify-center transition-transform hover:scale-110"
              style={{
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "rgba(0,0,0,0.6)",
                border: "1px solid rgba(255,255,255,0.28)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                zIndex: 3,
                cursor: "pointer",
                padding: 0,
              }}
            >
              {playing ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff">
                  <rect x="6" y="5" width="4" height="14" rx="1" />
                  <rect x="14" y="5" width="4" height="14" rx="1" />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
                  <path d="M8 5.5v13a1 1 0 0 0 1.55.83l10-6.5a1 1 0 0 0 0-1.66l-10-6.5A1 1 0 0 0 8 5.5z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Switcher label */}
        <div
          className="mt-10 text-center"
          style={{
            fontFamily: '"Chakra Petch", sans-serif',
            fontSize: 10,
            letterSpacing: "0.35em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.5)",
          }}
        >
          ↓ 別の変身例を見る ↓
        </div>

        {/* Pair thumbnails row */}
        <div
          className="kagem-scroll-row mt-6 flex gap-4 overflow-x-auto pb-4"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {BEFORE_AFTER_PAIRS.map((p) => {
            const active = p.id === currentId;
            return (
              <button
                type="button"
                key={p.id}
                onClick={() => handleSelect(p.id)}
                className={`kagem-material-card shrink-0 ${active ? "is-active" : ""}`}
                style={{
                  flex: "0 0 220px",
                  cursor: "pointer",
                  background: "transparent",
                  padding: 0,
                  scrollSnapAlign: "start",
                  border: "none",
                }}
                aria-pressed={active}
              >
                <div
                  className="overflow-hidden"
                  style={{
                    borderRadius: 12,
                    border: active
                      ? "2px solid #fff"
                      : "2px solid rgba(255,255,255,0.08)",
                    boxShadow: active
                      ? "0 0 0 4px rgba(255,255,255,0.12)"
                      : "none",
                    padding: 6,
                    background: "rgba(255,255,255,0.03)",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 4,
                  }}
                >
                  {/\.(png|jpe?g|webp|gif|avif)$/i.test(p.thumbBefore) ? (
                    <img
                      src={p.thumbBefore}
                      alt=""
                      className="w-full object-cover"
                      style={{ aspectRatio: "9 / 16", borderRadius: 6, background: "#000" }}
                    />
                  ) : (
                    <video
                      src={p.thumbBefore}
                      muted
                      loop
                      playsInline
                      autoPlay
                      className="w-full object-cover"
                      style={{ aspectRatio: "9 / 16", borderRadius: 6, background: "#000" }}
                    />
                  )}
                  {/\.(png|jpe?g|webp|gif|avif)$/i.test(p.thumbAfter) ? (
                    <img
                      src={p.thumbAfter}
                      alt=""
                      className="w-full object-cover"
                      style={{ aspectRatio: "9 / 16", borderRadius: 6, background: "#000" }}
                    />
                  ) : (
                    <video
                      src={p.thumbAfter}
                      muted
                      loop
                      playsInline
                      autoPlay
                      className="w-full object-cover"
                      style={{ aspectRatio: "9 / 16", borderRadius: 6, background: "#000" }}
                    />
                  )}
                </div>
                <div
                  className="mt-2 text-center"
                  style={{
                    fontFamily: '"Chakra Petch", sans-serif',
                    fontSize: 10.5,
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: active ? "#fff" : "rgba(255,255,255,0.5)",
                  }}
                >
                  {p.label}
                </div>
              </button>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 flex flex-col items-center">
          <p style={{ fontFamily: '"Noto Sans JP", sans-serif', fontSize: 14, color: "rgba(255,255,255,0.6)", marginBottom: 12, textAlign: "center", fontWeight: 500 }}>
            これがあなたにも起きる。
          </p>
          <Link
            to="/signup"
            className="kagem-cta-primary inline-flex items-center justify-center"
            style={{
              background: "#fff",
              color: "#000",
              padding: "16px 30px",
              borderRadius: 999,
              fontFamily: '"Chakra Petch", sans-serif',
              fontSize: 14,
              fontWeight: 500,
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
          >
            自分の動画と、好きな素材で試してみる →
          </Link>
          <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 20, fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
            {["会員登録1分", "月契約はいつでも解約OK", "素材は自動削除"].map((t) => (
              <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                {t}
              </span>
            ))}
          </div>
          <p
            className="mt-4 text-center"
            style={{
              fontFamily: '"Noto Sans JP", sans-serif',
              fontSize: 11,
              color: "rgba(255,255,255,0.32)",
              maxWidth: 520,
              lineHeight: 1.7,
            }}
          >
            ※素材は、あなた自身が権利を持つ画像・動画、または利用許諾を得たものをご使用ください。
          </p>
        </div>
      </div>
    </section>
  );
}

import ucIdolImg from "@/assets/usecases/uc-idol.jpg";
import ucAltselfImg from "@/assets/usecases/uc-altself.jpg";
import ucWeddingImg from "@/assets/usecases/uc-wedding.jpg";
import ucCharacterImg from "@/assets/usecases/uc-character.jpg";
import ucBrandImg from "@/assets/usecases/uc-brand.jpg";
import ucAnimeImg from "@/assets/usecases/uc-anime.jpg";

const USE_CASES: { title: string; desc: string; image: string }[] = [
  {
    title: "推しに変身してSNS投稿",
    desc: "大好きなアーティスト・俳優の素材で、自分が\"その人\"になった動画を作って投稿。",
    image: ucIdolImg,
  },
  {
    title: "別バージョンの自分を作る",
    desc: "年齢を変えた自分、性別違いの自分、もう1人の自分。素材を変えれば全部可能。",
    image: ucAltselfImg,
  },
  {
    title: "結婚式・誕生日の余興動画",
    desc: "主役を別の人物に変身させたサプライズ動画。お祝いに刺さる演出を簡単に。",
    image: ucWeddingImg,
  },
  {
    title: "AIで作ったオリジナルキャラで創作",
    desc: "画像生成で作ったあなただけのキャラに変身。VTuber的な創作活動の幅を広げる。",
    image: ucCharacterImg,
  },
  {
    title: "広告・ブランディング素材",
    desc: "モデル撮影せずに高品質な動画素材を量産。Standard以上のプランで商用利用可。",
    image: ucBrandImg,
  },
  {
    title: "アニメ・ゲームキャラへのなりきり",
    desc: "好きなキャラの素材で、本格的に世界観に入り込んだ動画を制作。",
    image: ucAnimeImg,
  },
];

function UseCases() {
  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "#000", padding: "clamp(72px, 11vw, 140px) 0" }}
    >
      <div className="relative z-10 mx-auto max-w-6xl px-4">
        <div className="text-center">
          <div
            style={{
              fontFamily: '"Chakra Petch", sans-serif',
              fontSize: 11,
              letterSpacing: "0.35em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.5)",
            }}
          >
            USE CASES
          </div>
          <h2
            className="mt-4"
            style={{
              fontFamily: '"Chakra Petch", "Noto Sans JP", sans-serif',
              fontWeight: 300,
              fontSize: "clamp(28px, 3.6vw, 44px)",
              letterSpacing: "0.04em",
              color: "#fff",
              lineHeight: 1.3,
            }}
          >
            アイディアがあれば、可能性は無限。
          </h2>
          <p
            className="mx-auto mt-4"
            style={{
              fontFamily: '"Noto Sans JP", sans-serif',
              fontSize: 15,
              color: "rgba(255,255,255,0.72)",
              maxWidth: 560,
              lineHeight: 1.85,
            }}
          >
            KAGEMは、あなたの「もう1人」を自由にデザインできるプラットフォーム。
          </p>
        </div>

        <div
          className="mt-14 grid"
          style={{
            gap: 18,
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          }}
        >
          {USE_CASES.map((u) => (
            <div
              key={u.title}
              className="kagem-usecase-card group/uc overflow-hidden"
              style={{
                background: "rgba(255,255,255,0.025)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 18,
              }}
            >
              <div className="relative aspect-[4/3] w-full overflow-hidden">
                <img
                  src={u.image}
                  alt={u.title}
                  loading="lazy"
                  width={768}
                  height={512}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover/uc:scale-105"
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.75) 100%)",
                  }}
                />
              </div>
              <div style={{ padding: "22px 24px 26px" }}>
                <h3
                  style={{
                    fontFamily: '"Noto Sans JP", sans-serif',
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#fff",
                    lineHeight: 1.5,
                  }}
                >
                  {u.title}
                </h3>
                <p
                  className="mt-2"
                  style={{
                    fontFamily: '"Noto Sans JP", sans-serif',
                    fontSize: 13,
                    color: "rgba(255,255,255,0.72)",
                    lineHeight: 1.75,
                  }}
                >
                  {u.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 48, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          <p style={{ fontFamily: '"Noto Sans JP", sans-serif', fontSize: 14, color: "rgba(255,255,255,0.6)", marginBottom: 16 }}>
            あなたのユースケースは、何ですか？
          </p>
          <Link
            to="/signup"
            className="kagem-inline-cta-ghost inline-flex items-center justify-center"
            style={{
              background: "transparent",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.4)",
              padding: "14px 26px",
              borderRadius: 999,
              fontFamily: '"Chakra Petch", sans-serif',
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: "0.02em",
              transition: "all 0.15s ease",
              textDecoration: "none",
            }}
          >
            自分の使い方で試す →
          </Link>
        </div>
      </div>
    </section>
  );
}

const COMPARISONS: { bad: { title: string; desc: string }; good: { title: string; desc: string } }[] = [
  {
    bad: { title: "顔だけのスワップ", desc: "顔の合成のみ。衣装も体型も背景もそのまま。違和感が残る。" },
    good: { title: "全身フル変身", desc: "顔・衣装・体型・動き・背景まで、独自AI技術で一気に変える次世代モデル。" },
  },
  {
    bad: { title: "静止画 or 無音動画", desc: "結果は画像 or 無音の短いクリップが中心。" },
    good: { title: "音声付き動画として出力", desc: "10秒のシネマティック動画。SNSへそのまま投稿可能。" },
  },
  {
    bad: { title: "テンプレ縛り・英語UI", desc: "用意されたキャラから選ぶ形式が多く、自由度が低い。" },
    good: { title: "素材自由・日本語完全対応", desc: "画像があれば誰にでも変身可能。UIも日本語で迷わない。" },
  },
];

function WhyKagem() {
  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "#000", padding: "clamp(72px, 11vw, 140px) 0" }}
    >
      <div className="relative z-10 mx-auto max-w-5xl px-4">
        <div className="text-center">
          <div
            style={{
              fontFamily: '"Chakra Petch", sans-serif',
              fontSize: 11,
              letterSpacing: "0.35em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.5)",
            }}
          >
            WHY KAGEM
          </div>
          <h2
            className="mt-4"
            style={{
              fontFamily: '"Chakra Petch", "Noto Sans JP", sans-serif',
              fontWeight: 300,
              fontSize: "clamp(28px, 3.6vw, 44px)",
              letterSpacing: "0.04em",
              color: "#fff",
              lineHeight: 1.3,
            }}
          >
            顔だけじゃない。全身を、変える。
          </h2>
          <p
            className="mx-auto mt-4"
            style={{
              fontFamily: '"Noto Sans JP", sans-serif',
              fontSize: 15,
              color: "rgba(255,255,255,0.72)",
              maxWidth: 640,
              lineHeight: 1.85,
            }}
          >
            他のAIアプリは顔のスワップだけ。KAGEMは独自AI技術で衣装・体型・動き・背景まで含めた&quot;全身変身&quot;動画を生成します。
          </p>
        </div>

        <div className="mt-14 space-y-3 sm:space-y-5">
          {COMPARISONS.map((pair, i) => (
            <div
              key={i}
              className="grid grid-cols-2 gap-2 sm:gap-4"
            >
              {/* BAD */}
              <div
                className="p-3 sm:p-7"
                style={{
                  borderRadius: 16,
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.015)",
                }}
              >
                <div
                  style={{
                    fontFamily: '"Chakra Petch", sans-serif',
                    fontSize: 10,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.32)",
                  }}
                >
                  他のAI
                </div>
                <h4
                  className="mt-2 sm:mt-3"
                  style={{
                    fontFamily: '"Noto Sans JP", sans-serif',
                    fontSize: "clamp(13px, 3.6vw, 17px)",
                    fontWeight: 500,
                    color: "rgba(255,255,255,0.5)",
                    lineHeight: 1.5,
                  }}
                >
                  {pair.bad.title}
                </h4>
                <p
                  className="mt-1.5 sm:mt-2"
                  style={{
                    fontFamily: '"Noto Sans JP", sans-serif',
                    fontSize: "clamp(11px, 3vw, 13px)",
                    color: "rgba(255,255,255,0.32)",
                    lineHeight: 1.7,
                  }}
                >
                  {pair.bad.desc}
                </p>
              </div>

              {/* GOOD */}
              <div
                className="p-3 sm:p-7"
                style={{
                  borderRadius: 16,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.05)",
                  boxShadow: "0 0 30px rgba(255,255,255,0.04)",
                }}
              >
                <div
                  style={{
                    fontFamily: '"Chakra Petch", sans-serif',
                    fontSize: 10,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    color: "#fff",
                  }}
                >
                  KAGEM
                </div>
                <h4
                  className="mt-2 sm:mt-3"
                  style={{
                    fontFamily: '"Noto Sans JP", sans-serif',
                    fontSize: "clamp(13px, 3.6vw, 17px)",
                    fontWeight: 600,
                    color: "#fff",
                    lineHeight: 1.5,
                  }}
                >
                  {pair.good.title}
                </h4>
                <p
                  className="mt-1.5 sm:mt-2"
                  style={{
                    fontFamily: '"Noto Sans JP", sans-serif',
                    fontSize: "clamp(11px, 3vw, 13px)",
                    color: "rgba(255,255,255,0.72)",
                    lineHeight: 1.7,
                  }}
                >
                  {pair.good.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 48, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          <p style={{ fontFamily: '"Noto Sans JP", sans-serif', fontSize: 14, color: "rgba(255,255,255,0.6)", marginBottom: 16 }}>
            違いは、試せばわかる。
          </p>
          <Link
            to="/signup"
            className="kagem-inline-cta-primary inline-flex items-center justify-center"
            style={{
              background: "#fff",
              color: "#000",
              padding: "14px 26px",
              borderRadius: 999,
              fontFamily: '"Chakra Petch", sans-serif',
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "0.02em",
              transition: "all 0.15s ease",
              textDecoration: "none",
            }}
          >
            違いを体験する →
          </Link>
        </div>
      </div>
    </section>
  );
}

// TODO: 実績数値があれば差し替え（生成本数・ユーザー数など）
const SOCIAL_PROOF_STATS: { value: string; label: string }[] = [
  { value: "1080p", label: "高画質出力" },
  { value: "10秒", label: "音声付き動画" },
  { value: "約3分", label: "で生成完了" },
  { value: "月300〜", label: "クレジット付与" },
];

function SocialProof() {
  return (
    <section
      style={{
        background: "#000",
        padding: "88px 0",
        borderTop: "1px solid rgba(255,255,255,0.08)",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div className="mx-auto max-w-6xl px-4">
        <div className="grid grid-cols-2 gap-y-10 md:grid-cols-4">
          {SOCIAL_PROOF_STATS.map((s) => (
            <div key={s.label} className="text-center">
              <div
                style={{
                  fontFamily: '"Chakra Petch", sans-serif',
                  fontSize: 48,
                  fontWeight: 300,
                  color: "#fff",
                  letterSpacing: "-0.02em",
                  textShadow: "0 0 30px rgba(255,255,255,0.25)",
                  lineHeight: 1.1,
                }}
              >
                {s.value}
              </div>
              <div
                className="mt-1"
                style={{
                  fontFamily: '"Chakra Petch", sans-serif',
                  fontSize: 11,
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.5)",
                  marginTop: 4,
                }}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const features = [
  {
    icon: Film,
    emoji: "🎬",
    title: "簡単3ステップ",
    desc: "画像と動画をアップ → 品質を選ぶ → 完成。面倒な設定は不要です。",
  },
  {
    icon: Bot,
    emoji: "🤖",
    title: "複数AIモデル対応",
    desc: "最先端のAIモデルに対応。用途に応じて最適なモデルを使い分け。",
  },
  {
    icon: Wallet,
    emoji: "💰",
    title: "クレジット制で柔軟",
    desc: "使った分だけ消費するクレジット制。月契約はいつでも変更・解約OK。",
  },
];

function Features() {
  return (
    <section className="px-4 py-24 sm:py-32">
      <div className="mx-auto max-w-6xl">
        <motion.div {...fadeInUp} className="mb-16 text-center">
          <h2 className="text-4xl font-bold tracking-tight sm:text-5xl">3つの特徴</h2>
          <p className="mt-4 text-muted-foreground">誰でもすぐに、本格的な変身動画が作れます。</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              {...fadeInUp}
              transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
            >
              <Card className="h-full border-border/60 p-8 transition-all hover:-translate-y-1 hover:shadow-xl">
                <div className="bg-gradient-brand mb-5 flex h-14 w-14 items-center justify-center rounded-2xl text-2xl">
                  {f.emoji}
                </div>
                <h3 className="mb-2 text-xl font-bold">{f.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Demo() {
  return (
    <section id="demo" className="bg-secondary/30 px-4 py-24 sm:py-32">
      <div className="mx-auto max-w-6xl">
        <motion.div {...fadeInUp} className="mb-12 text-center">
          <Badge variant="secondary" className="mb-3">
            実際の生成例
          </Badge>
          <h2 className="text-4xl font-bold tracking-tight sm:text-5xl">Before / After</h2>
          <p className="mt-4 text-muted-foreground">写真1枚から、こんな動画が作れます。</p>
        </motion.div>

        <motion.div
          {...fadeInUp}
          className="grid grid-cols-1 gap-6 md:grid-cols-2"
        >
          <div className="relative">
            <Badge className="absolute left-3 top-3 z-10" variant="outline">
              Before
            </Badge>
            <div className="aspect-video overflow-hidden rounded-2xl border border-border/60">
              <img
                src={beforeAfterOriginal}
                alt="生成元の写真：山と湖を背景に花畑に立つ女性"
                width={1672}
                height={941}
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          <div className="relative">
            <Badge className="bg-gradient-brand absolute left-3 top-3 z-10 border-0">After</Badge>
            <div className="aspect-video overflow-hidden rounded-2xl border border-border/60 bg-black">
              <LazyVideo
                src="/before-after-generated.mp4"
                className="h-full w-full object-cover"
              />
            </div>
          </div>

        </motion.div>
      </div>
    </section>
  );
}

const allPlans = [
  {
    name: "お試し",
    price: "¥500",
    suffix: "",
    desc: "まず1本だけ変身を試したい方に",
    features: [
      "全身変身 1本",
      "音声付き / 10秒 / 1080p",
      "会員登録1分",
      "1人1回限り",
    ],
    planId: "trial",
    tier: "trial" as const,
  },
  {
    name: "Light",
    price: "¥2,980",
    suffix: "/月",
    desc: "月数回楽しむ方に",
    features: ["月300クレジット", "標準品質", "ウォーターマーク無", "商用利用OK"],
    planId: "light",
    tier: "normal" as const,
  },
  {
    name: "Standard",
    price: "¥8,800",
    suffix: "/月",
    desc: "定期的に投稿する方",
    features: ["月1,000クレジット", "高品質モデル", "優先処理", "商用利用OK"],
    planId: "standard",
    tier: "recommended" as const,
  },
  {
    name: "Pro",
    price: "¥24,800",
    suffix: "/月",
    desc: "本格制作のクリエイター",
    features: ["月3,000クレジット", "全AIモデル", "最優先処理", "API連携"],
    planId: "pro",
    tier: "normal" as const,
  },
];

const plans = allPlans.filter((p) => SHOW_TRIAL_PLAN || p.tier !== "trial");

function Pricing() {
  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "#000", padding: "clamp(72px, 11vw, 140px) 0" }}
    >
      <div className="relative z-10 mx-auto max-w-6xl px-4">
        <div className="text-center">
          <div
            style={{
              fontFamily: '"Chakra Petch", sans-serif',
              fontSize: 11,
              letterSpacing: "0.35em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.5)",
            }}
          >
            PRICING
          </div>
          <h2
            className="mt-4"
            style={{
              fontFamily: '"Chakra Petch", "Noto Sans JP", sans-serif',
              fontWeight: 300,
              fontSize: "clamp(28px, 3.6vw, 44px)",
              letterSpacing: "0.04em",
              color: "#fff",
              lineHeight: 1.3,
            }}
          >
            用途に合わせて選べるプラン。
          </h2>
          <p
            className="mx-auto mt-4"
            style={{
              fontFamily: '"Noto Sans JP", sans-serif',
              fontSize: 15,
              color: "rgba(255,255,255,0.72)",
              maxWidth: 640,
              lineHeight: 1.85,
            }}
          >
            クレジット制で、変身も動画生成も画像生成も自由に使えます。月契約はいつでも解約OK。
          </p>
        </div>

        <div
          className="mt-16 grid gap-4"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}
        >
          {plans.map((p) => {
            const isTrial = p.tier === "trial";
            const isRecommended = p.tier === "recommended";

            const cardStyle: CSSProperties = isTrial
              ? {
                  padding: "28px 22px",
                  background: "rgba(255,255,255,0.05)",
                  border: "2px solid rgba(255,255,255,0.35)",
                  borderRadius: 18,
                  boxShadow:
                    "0 30px 80px rgba(255,255,255,0.08), 0 0 0 1px rgba(255,255,255,0.04)",
                }
              : isRecommended
                ? {
                    padding: "28px 22px",
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    borderRadius: 18,
                  }
                : {
                    padding: "28px 22px",
                    background: "rgba(255,255,255,0.025)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 18,
                  };

            return (
              <div key={p.name} className="relative flex flex-col" style={cardStyle}>
                {isTrial && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2"
                    style={{
                      top: -14,
                      background: "#fff",
                      color: "#000",
                      padding: "5px 14px",
                      borderRadius: 999,
                      fontFamily: '"Chakra Petch", sans-serif',
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    ⚡ まずはここから
                  </div>
                )}
                {isRecommended && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2"
                    style={{
                      top: -12,
                      background: "rgba(0,0,0,0.9)",
                      color: "#fff",
                      padding: "4px 12px",
                      borderRadius: 999,
                      border: "1px solid rgba(255,255,255,0.5)",
                      fontFamily: '"Chakra Petch", sans-serif',
                      fontSize: 10,
                      fontWeight: 500,
                      letterSpacing: "0.25em",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    おすすめ
                  </div>
                )}

                <h3
                  style={{
                    fontFamily: '"Noto Sans JP", sans-serif',
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#fff",
                  }}
                >
                  {p.name}
                </h3>
                <p
                  className="mt-1"
                  style={{
                    fontFamily: '"Noto Sans JP", sans-serif',
                    fontSize: 12,
                    color: "rgba(255,255,255,0.5)",
                    lineHeight: 1.6,
                    minHeight: 38,
                  }}
                >
                  {p.desc}
                </p>

                <div className="mt-4 flex items-baseline gap-1">
                  <span
                    style={{
                      fontFamily: '"Chakra Petch", sans-serif',
                      fontSize: isTrial ? 42 : 32,
                      fontWeight: isTrial ? 600 : 400,
                      color: "#fff",
                      letterSpacing: "-0.01em",
                      lineHeight: 1.1,
                      textShadow: isTrial
                        ? "0 0 24px rgba(255,255,255,0.3)"
                        : undefined,
                    }}
                  >
                    {p.price}
                  </span>
                  <span
                    style={{
                      fontFamily: '"Chakra Petch", sans-serif',
                      fontSize: 13,
                      color: "rgba(255,255,255,0.5)",
                    }}
                  >
                    {p.suffix}
                  </span>
                </div>

                <ul className="mt-5 flex-1 space-y-2.5">
                  {p.features.map((feat) => (
                    <li
                      key={feat}
                      className="flex items-start gap-2"
                      style={{
                        fontFamily: '"Noto Sans JP", sans-serif',
                        fontSize: 13,
                        color: "rgba(255,255,255,0.85)",
                        lineHeight: 1.6,
                      }}
                    >
                      <Check
                        className="mt-0.5 h-4 w-4 shrink-0"
                        style={{ color: "rgba(255,255,255,0.85)" }}
                      />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/signup"
                  search={{ plan: p.planId } as never}
                  className={`mt-6 inline-flex w-full items-center justify-center ${
                    isTrial ? "kagem-cta-primary" : "kagem-cta-secondary"
                  }`}
                  style={
                    isTrial
                      ? {
                          background: "#fff",
                          color: "#000",
                          padding: "13px 18px",
                          borderRadius: 999,
                          fontFamily: '"Chakra Petch", sans-serif',
                          fontSize: 13,
                          fontWeight: 600,
                          transition:
                            "transform 0.2s ease, box-shadow 0.2s ease",
                        }
                      : {
                          background: "transparent",
                          color: "#fff",
                          padding: "13px 18px",
                          borderRadius: 999,
                          border: "1px solid rgba(255,255,255,0.18)",
                          fontFamily: '"Chakra Petch", sans-serif',
                          fontSize: 13,
                          fontWeight: 500,
                          transition:
                            "border-color 0.2s ease, background 0.2s ease",
                        }
                  }
                >
                  {isTrial ? "このプランで始める →" : "このプランで始める"}
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const faqs = [
  {
    q: "自分の動画と素材があれば、どんな人物にでも変身できますか？",
    a: "はい。あなたが用意した素材（写真・動画・AI生成画像など）に基づいて、その人物の見た目に変身します。固定のテンプレートはなく、画像が用意できれば変身先は自由です。",
  },
  {
    q: "著作権・肖像権は大丈夫ですか？",
    a: "素材は、あなた自身が権利を持つ画像・動画、または利用が許諾されているものをご使用ください。第三者の肖像権・著作権を侵害する利用は規約で禁止されています。",
  },
  {
    q: "どんな素材が使えますか？",
    a: "全身が写った写真や動画、AIで生成した画像、イラストなど幅広く対応します。鮮明で全身が映っているほど精度が上がります。",
  },
  {
    q: "顔だけじゃなくて、本当に全身が変身するんですか？",
    a: "はい。独自開発の次世代AI技術で、顔・衣装・体型・動き・背景まで含めた\"全身\"を1本の動画で変えます。",
  },
  {
    q: "生成にどれくらい時間がかかりますか？",
    a: "通常1〜3分。混雑時は最大10分ほど。Pro/Standardプランは優先処理されます。",
  },
  {
    q: "商用利用できますか？",
    a: "Standard以上のプランで商用利用可能です。",
  },
  {
    q: "アップロードした素材は安全ですか？",
    a: "暗号化通信でアップロードされ、生成後は一定期間で自動削除されます。第三者への提供は一切ありません。",
  },
  {
    q: "解約はいつでもできますか？",
    a: "設定画面からいつでも解約リクエスト可能です。月契約は次回課金日で解約、年契約（12ヶ月縛りの月払い）は12ヶ月目の課金完了時に自動解約となります。違約金は一切ありません。",
  },
];

function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return (
    <section
      style={{
        background: "var(--bg-2, #0a0a0a)",
        borderTop: "1px solid var(--line, rgba(255,255,255,0.08))",
        padding: "clamp(64px, 10vw, 120px) 0",
      }}
    >
      <div className="mx-auto max-w-3xl px-4">
        <div style={{ marginBottom: 48, textAlign: "center" }}>
          <div
            style={{
              fontFamily: "'Chakra Petch', sans-serif",
              fontSize: 11,
              letterSpacing: "0.35em",
              textTransform: "uppercase",
              color: "var(--text-3, rgba(255,255,255,0.5))",
              marginBottom: 16,
            }}
          >
            FAQ
          </div>
          <h2
            style={{
              fontSize: "clamp(28px, 4vw, 40px)",
              fontWeight: 500,
              color: "#fff",
              letterSpacing: "-0.02em",
            }}
          >
            よくある質問
          </h2>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {faqs.map((item, i) => {
            const open = openIndex === i;
            return (
              <div
                key={i}
                className="kagem-faq-item"
                style={{
                  background: "var(--panel, rgba(255,255,255,0.03))",
                  border: "1px solid var(--line, rgba(255,255,255,0.08))",
                  borderRadius: 12,
                  overflow: "hidden",
                  transition: "border-color 0.2s ease",
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(open ? null : i)}
                  className="faq-q"
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    padding: "18px 24px",
                    background: "transparent",
                    border: "none",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 500,
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <span>{item.q}</span>
                  <span
                    style={{
                      display: "inline-block",
                      fontSize: 18,
                      lineHeight: 1,
                      color: "var(--text-2, rgba(255,255,255,0.72))",
                      transform: open ? "rotate(45deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  >
                    +
                  </span>
                </button>
                <div
                  className={`faq-a${open ? " open" : ""}`}
                  style={{
                    display: open ? "block" : "none",
                    padding: "0 24px 20px",
                    color: "var(--text-2, rgba(255,255,255,0.72))",
                    fontSize: 13.5,
                    lineHeight: 1.9,
                  }}
                >
                  {item.a}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 56, paddingTop: 16, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          <p style={{ fontFamily: '"Noto Sans JP", sans-serif', fontSize: 16, fontWeight: 500, color: "#fff", marginBottom: 8 }}>
            疑問が解消したら、はじめよう。
          </p>
          <p style={{ fontFamily: '"Noto Sans JP", sans-serif', fontSize: 13, color: "rgba(255,255,255,0.6)", marginBottom: 20 }}>
            クレジット制で全身変身を体験できます。
          </p>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap", justifyContent: "center" }}>
            <Link
              to="/signup"
              className="kagem-inline-cta-primary inline-flex items-center justify-center"
              style={{
                background: "#fff",
                color: "#000",
                padding: "14px 28px",
                borderRadius: 999,
                fontFamily: '"Chakra Petch", sans-serif',
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: "0.02em",
                transition: "all 0.15s ease",
                textDecoration: "none",
              }}
            >
              試してみる →
            </Link>
            <Link
              to="/signup"
              style={{
                background: "transparent",
                color: "rgba(255,255,255,0.6)",
                fontFamily: '"Noto Sans JP", sans-serif',
                fontSize: 13,
                marginLeft: 16,
                textDecoration: "none",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "underline"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
            >
              まずはサンプルを見る
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  const particlesRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = particlesRef.current;
    if (!host) return;
    host.innerHTML = "";
    const count = 60;
    for (let i = 0; i < count; i++) {
      const p = document.createElement("span");
      const size = Math.random() * 2 + 1;
      const duration = Math.random() * 10 + 8;
      const delay = Math.random() * 10;
      p.style.cssText = `
        position:absolute;
        left:${Math.random() * 100}%;
        top:${Math.random() * 100}%;
        width:${size}px;
        height:${size}px;
        border-radius:50%;
        background:rgba(255,255,255,${Math.random() * 0.5 + 0.2});
        box-shadow:0 0 ${size * 3}px rgba(255,255,255,0.5);
        animation:kagemFloat ${duration}s ease-in-out ${delay}s infinite;
        pointer-events:none;
      `;
      host.appendChild(p);
    }
  }, []);

  return (
    <section
      style={{
        position: "relative",
        overflow: "hidden",
        background: "#000",
        padding: "clamp(72px, 11vw, 140px) 24px",
        textAlign: "center",
      }}
    >
      {/* Glow */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(50% 70% at 50% 50%, rgba(255,255,255,0.08), transparent 70%)",
          pointerEvents: "none",
        }}
      />
      {/* Dot grid */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          maskImage:
            "radial-gradient(ellipse 60% 60% at 50% 50%, #000 40%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 60% 60% at 50% 50%, #000 40%, transparent 100%)",
          pointerEvents: "none",
        }}
      />
      {/* Particles */}
      <div
        id="particlesCTA"
        ref={particlesRef}
        aria-hidden
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      />

      <div style={{ position: "relative", maxWidth: 880, margin: "0 auto" }}>
        <div
          style={{
            fontFamily: "'Chakra Petch', sans-serif",
            fontSize: 11,
            letterSpacing: "0.35em",
            textTransform: "uppercase",
            color: "var(--text-3, rgba(255,255,255,0.5))",
            marginBottom: 24,
          }}
        >
          GENERATE ANOTHER YOU
        </div>
        <h2
          style={{
            fontFamily: "'Chakra Petch', sans-serif",
            fontWeight: 300,
            fontSize: "clamp(32px, 5vw, 56px)",
            lineHeight: 1.3,
            letterSpacing: "-0.02em",
            color: "#fff",
            margin: 0,
          }}
        >
          あなたの動画と、画像1枚。
          <br />
          <span
            style={{
              fontWeight: 600,
              textShadow: "0 0 40px rgba(255,255,255,0.45)",
            }}
          >
            もう1人の自分は、すぐそこに。
          </span>
        </h2>
        <p
          style={{
            marginTop: 24,
            fontSize: 15,
            color: "var(--text-2, rgba(255,255,255,0.72))",
          }}
        >
          初めての全身変身を、いますぐ。会員登録は1分。
        </p>

        <div
          style={{
            marginTop: 40,
            display: "flex",
            gap: 14,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Link
            to="/signup"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "16px 32px",
              background: "#fff",
              color: "#000",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 600,
              textDecoration: "none",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              boxShadow: "0 0 40px rgba(255,255,255,0.2)",
            }}
          >
            はじめる →
          </Link>
          <a
            href="#showcase"
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "16px 28px",
              background: "transparent",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.32)",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 500,
              textDecoration: "none",
              transition: "border-color 0.2s ease, background 0.2s ease",
            }}
          >
            サンプルをもう一度見る
          </a>
        </div>

        <div
          style={{
            marginTop: 32,
            display: "flex",
            gap: 24,
            justifyContent: "center",
            flexWrap: "wrap",
            fontSize: 12,
            color: "var(--text-3, rgba(255,255,255,0.5))",
          }}
        >
          <span>✓ 月契約はいつでも解約OK</span>
          <span>✓ 素材は自動削除</span>
          <span>✓ 日本語完全対応</span>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border/60 bg-background px-4 py-12">
      <div className="mx-auto max-w-6xl">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          <div className="col-span-2 md:col-span-1">
            <BrandLogo size="sm" />
            <p className="mt-3 text-xs text-muted-foreground">
              AIで動画の主役をあなたに。
            </p>
            <div className="mt-4 flex gap-3">
              <a
                href="#"
                aria-label="TikTok"
                className="text-muted-foreground transition hover:text-foreground"
              >
                <Music2 className="h-5 w-5" />
              </a>
              <a
                href="#"
                aria-label="Instagram"
                className="text-muted-foreground transition hover:text-foreground"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="#"
                aria-label="YouTube"
                className="text-muted-foreground transition hover:text-foreground"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold">サービス</h4>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li>
                <Link to="/pricing" className="hover:text-foreground">
                  料金プラン
                </Link>
              </li>
              <li>
                <Link to="/how-to-use" className="hover:text-foreground">
                  使い方
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-foreground">
                  お問い合わせ
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold">サポート</h4>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li>
                <Link to="/contact" className="hover:text-foreground">
                  ヘルプセンター
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-foreground">
                  お問い合わせ
                </Link>
              </li>
              <li>
                <Link to="/report" className="hover:text-foreground">
                  不適切なコンテンツを通報
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold">法的情報</h4>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li>
                <Link to="/terms" className="hover:text-foreground">
                  利用規約
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-foreground">
                  プライバシーポリシー
                </Link>
              </li>
              <li>
                <Link to="/tokushoho" className="hover:text-foreground">
                  特定商取引法表記
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-10 border-t border-border/60 pt-6 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} KAGEM. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
